

/**
 * Global cache with TTL
 * - In-memory + optional localStorage (when available)
 * - Safe for SSR and private mode (all storage ops try/catch)
 * - JSON parse/stringify guarded
 *
 * Usage:
 *   import cache from '@/services/globalCache';
 *   const data = await cache.getWith(key('nextShow', stationId), 5*60*1000, () => fetchNextShow());
 */

const NAMESPACE = 'astra_cache::';

// Detect localStorage availability once
const hasLocalStorage = (() => {
  try {
    if (typeof window === 'undefined' || !('localStorage' in window)) return false;
    const t = '__gc_test__';
    window.localStorage.setItem(t, '1');
    window.localStorage.removeItem(t);
    return true;
  } catch {
    return false;
  }
})();

const memoryStore = new Map();

function now() {
  return Date.now();
}

function makeKey(key) {
  return `${NAMESPACE}${key}`;
}

function safeGetItem(k) {
  if (!hasLocalStorage) return null;
  try {
    return window.localStorage.getItem(k);
  } catch {
    return null;
  }
}

function safeSetItem(k, v) {
  if (!hasLocalStorage) return;
  try {
    window.localStorage.setItem(k, v);
  } catch {
    // ignore quota / private mode
  }
}

function safeRemoveItem(k) {
  if (!hasLocalStorage) return;
  try {
    window.localStorage.removeItem(k);
  } catch {
    // ignore
  }
}

// ---- Core API ----

function set(key, value, ttlMs) {
  const expiresAt = ttlMs ? now() + ttlMs : null;
  const entry = { value, expiresAt };
  memoryStore.set(key, entry);

  // persist light copy
  try {
    const toPersist = JSON.stringify(entry);
    safeSetItem(makeKey(key), toPersist);
  } catch {
    // ignore serialization errors
  }
}

function get(key) {
  // 1) memory
  const mem = memoryStore.get(key);
  if (mem) {
    if (mem.expiresAt === null || mem.expiresAt > now()) return mem.value;
    // expired
    memoryStore.delete(key);
  }

  // 2) storage
  const raw = safeGetItem(makeKey(key));
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && (parsed.expiresAt === null || parsed.expiresAt > now())) {
      // rehydrate memory for speed
      memoryStore.set(key, parsed);
      return parsed.value;
    }
  } catch {
    // corrupted entry, drop it
  }
  safeRemoveItem(makeKey(key));
  return null;
}

function hasValid(key) {
  return get(key) !== null;
}

function invalidate(key) {
  memoryStore.delete(key);
  safeRemoveItem(makeKey(key));
}

/**
 * Fetch from cache or compute and store.
 * Ensures `fetcher` returns a value; if it returns undefined/null, we don't cache.
 */
async function getWith(key, ttlMs, fetcher) {
  const cached = get(key);
  if (cached !== null && cached !== undefined) return cached;

  const value = await Promise.resolve(fetcher && fetcher());
  if (value !== undefined && value !== null) set(key, value, ttlMs);
  return value ?? null;
}

/**
 * Helper to build namespaced keys, avoiding collisions.
 * e.g., key('nextShow', stationId) => "nextShow::1"
 */
function key(base, ...parts) {
  return [base, ...parts].join('::');
}

export default {
  set,
  get,
  hasValid,
  invalidate,
  getWith,
  key,
};